import React from "react";

import { redirect } from "react-router-dom";
import { createBrowserRouter } from "react-router-dom";

import Login from "./components/Pages/Login/Login";
import FinalPage from "./components/Pages/Final/FinalPage";
import StartPage from "./components/Pages/StartPage/StartPage";
import ClueBoard from "./components/Pages/ClueBoard/ClueBoard";
import ErrorPage from "./components/Pages/ErrorPage/ErrorPage";
import RulesPage from "./components/Pages/RulesPage/RulesPage";
import LockGuard from "./components/shared/LockGuard/LockGuard";
import AuthWrapper from "./components/shared/AuthWrapper/AuthWrapper";
import GameFinishP2 from "./components/Pages/GameFinishP2/GameFinishP2";
import MissionFailed from "./components/Pages/MissionFailed/MissionFailed";
import M3Rookie from "./components/Missions/Mission3/Levels/M3Rookie/M3Rookie";
import ExitRouteGuard from "./components/shared/ExitRouteGuard/ExitRouteGuard";
import MissionComplete from "./components/Pages/MissionComplete/MissionComplete";
import M3Intermediate from "./components/Missions/Mission3/Levels/M3Intermediate/M3Intermediate";

import { syncOrchestrator } from "./api/services/syncOrchestrator";

import store from "./redux/store";
import { storedUser } from "./utils/storage";
import { clearAllPins } from "./redux/slices/pinclueSlice";
import { clearUser, resumeGameThunk } from "./redux/slices/createUserSlice";
import { ensureEligibleThenInitialize } from "./redux/slices/exitFlowSlice";

// Mission 1 imports
const Mission1 = React.lazy(() =>
  import("./components/Pages/Mission1/Mission1")
);
const M1Rookie = React.lazy(() =>
  import("./components/Missions/Mission1/Levels/M1Rookie/M1Rookie")
);
const M1Advanced = React.lazy(() =>
  import("./components/Missions/Mission1/Levels/M1Advance/M1Advance")
);
const M1Intermediate = React.lazy(() =>
  import("./components/Missions/Mission1/Levels/M1Intermediate/M1Intermediate")
);
//Mission 2 imports

const M2RookieGame = React.lazy(() =>
  import("./components/Missions/Mission2/Levels/M2Rookie/M2Rookie")
);
const M2Intermediate = React.lazy(() =>
  import("./components/Missions/Mission2/Levels/M2Intermediate/M2Intermediate")
);
const M2Advance = React.lazy(() =>
  import("./components/Missions/Mission2/Levels/M2Advance/M2Advance")
);
const GenerateInvestigationCode = React.lazy(() =>
  import(
    "./components/Pages/GenrateInvestigationCode/GenerateInvestigationCode"
  )
);

// Mission 3 imports
const Mission3 = React.lazy(() =>
  import("./components/Pages/Mission3/Mission3")
);

const M3Advance = React.lazy(() =>
  import("./components/Missions/Mission3/Levels/M3Advance/M3Advance")
);

// Mission 4 imports
const Mission4 = React.lazy(() =>
  import("./components/Pages/Mission4/Mission4")
);
const M4Rookie = React.lazy(() =>
  import("./components/Missions/Mission4/Levels/M4Rookie/M4Rookie")
);
const M4Advance = React.lazy(() =>
  import("./components/Missions/Mission4/Levels/M4Advance/M4Advance")
);
const M4Intermediate = React.lazy(() =>
  import("./components/Missions/Mission4/Levels/M4Intermediate/M4Intermediate")
);

// Lazy loaded components

const Map = React.lazy(() => import("./components/Pages/Map/Map"));
const SettingsPanel = React.lazy(() =>
  import("./components/Pages/SettingsPanel/SettingsPanel")
);
const MissionAccomplishments = React.lazy(() =>
  import("./components/Pages/MissionAccomplishments/MissionAccomplishments")
);
const Mission2 = React.lazy(() =>
  import("./components/Pages/Mission2/Mission2")
);

const GameFinish = React.lazy(() =>
  import("./components/Pages/GameFinish/GameFinish")
);

const OnBoard = React.lazy(() => import("./components/Pages/OnBoard/OnBoard"));

const rootLoader = async () => {
  try {
    if (storedUser?.authToken && storedUser?.systemName) {
      // 1) restore user/session
      const resume = await store.dispatch(
        resumeGameThunk({
          authToken: storedUser.authToken,
          systemName: storedUser.systemName,
        })
      );
      if (syncOrchestrator) {
        await syncOrchestrator.start();
      }
      if (resumeGameThunk.fulfilled.match(resume)) {
        // 2) ask server where the Exit flow is (and init if needed)
        const exitAction = await store.dispatch(
          ensureEligibleThenInitialize({
            systemName: storedUser.systemName,
            authToken: storedUser.authToken,
          })
        );

        // 3) choose destination
        if (ensureEligibleThenInitialize.fulfilled.match(exitAction)) {
          const state = exitAction.payload?.state;
          const alreadyInitiated =
            exitAction.payload?.alreadyInitiated === true;
          // early/phase-1 → /gameFinish, late/phase-2 → /gameFinishP2
          if (state === "not_started" && alreadyInitiated) {
            return redirect("/gameFinishP2");
          }
          if (
            state === "initiated" ||
            state === "montage_done" ||
            state === "details_submitted" ||
            state === "reveal_ready"
          ) {
            return redirect("/gameFinishP2");
          }
          if (state === "reveal_done") {
            return redirect("/finals");
          }

          // not started → normal map
          return redirect("/map");
        }

        // if status/init failed, fall back to map
        return redirect("/map");
      }

      // resume failed -> clear local & fall through
      localStorage.removeItem("userCredentials");
      localStorage.removeItem("clueState");
      store.dispatch(clearUser());
      store.dispatch(clearAllPins());
    } else {
      // no stored creds -> clear
      localStorage.removeItem("userCredentials");
      localStorage.removeItem("clueState");
      store.dispatch(clearUser());
      store.dispatch(clearAllPins());
    }
  } catch (e) {
    // safety: clear & continue
    localStorage.removeItem("userCredentials");
    localStorage.removeItem("clueState");
    store.dispatch(clearUser());
    store.dispatch(clearAllPins());
  }

  // no redirect -> render route normally
  // return null;
};

const FallBack = () => {
  return <div>Loading...</div>;
};

const router = createBrowserRouter([
  {
    // element: <ProtectedRoute />,
    hydrateFallbackElement: <FallBack />,
    children: [
      {
        path: "/",
        element: <StartPage />,
        loader: rootLoader,
      },
      {
        path: "/generateInvestigationCode",
        element: <GenerateInvestigationCode />,
      },
      { path: "/rules", element: <RulesPage /> },
      {
        element: <AuthWrapper />,
        children: [
          // All gameplay/routes that should be blocked once Exit is locked
          {
            element: <LockGuard />,
            children: [
              { path: "/map", element: <Map /> },

              { path: "/SecretsoftheCircuit", element: <Mission1 /> },
              { path: "/SecretsoftheCircuit/rookie", element: <M1Rookie /> },
              {
                path: "/SecretsoftheCircuit/intermediate",
                element: <M1Intermediate />,
              },
              {
                path: "/SecretsoftheCircuit/advanced",
                element: <M1Advanced />,
              },

              { path: "/accessDenied", element: <Mission2 /> },
              { path: "/accessDenied/rookie", element: <M2RookieGame /> },
              {
                path: "/accessDenied/intermediate",
                element: <M2Intermediate />,
              },
              { path: "/accessDenied/advanced", element: <M2Advance /> },

              { path: "/digitalBreadcrumbs", element: <Mission3 /> },
              { path: "/digitalBreadcrumbs/rookie", element: <M3Rookie /> },
              {
                path: "/digitalBreadcrumbs/intermediate",
                element: <M3Intermediate />,
              },
              { path: "/digitalBreadcrumbs/advanced", element: <M3Advance /> },

              { path: "/visualDecipher", element: <Mission4 /> },
              { path: "/visualDecipher/rookie", element: <M4Rookie /> },
              {
                path: "/visualDecipher/intermediate",
                element: <M4Intermediate />,
              },
              { path: "/visualDecipher/advanced", element: <M4Advance /> },

              {
                path: "/missionAccomplishments",
                element: <MissionAccomplishments />,
              },

              { path: "/clueboard", element: <ClueBoard /> },
              { path: "/settings", element: <SettingsPanel /> },
              { path: "/missionComplete", element: <MissionComplete /> },
              { path: "/missionFailed", element: <MissionFailed /> },
              { path: "/on-board", element: <OnBoard /> },
            ],
          },

          //  Finish flow routes remain outside LockGuard, but still use ExitRouteGuard
          {
            path: "/gameFinish",
            element: (
              <ExitRouteGuard>
                <GameFinish />
              </ExitRouteGuard>
            ),
          },
          {
            path: "/gameFinishP2",
            element: (
              <ExitRouteGuard>
                <GameFinishP2 />
              </ExitRouteGuard>
            ),
          },
          {
            path: "/finals",
            element: (
              <ExitRouteGuard>
                <FinalPage />
              </ExitRouteGuard>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <ErrorPage />,
  },
  { path: "/login", element: <Login /> },
]);

export default router;
